import { logger } from '@accedo/logger';

export interface RequestParams {
  [key: string]: unknown;
  pageSize?: number; // defaults to 20
  /**
   * Accedo OVP supports availableDate | publishedDate.
   * For getCategories it supports id | title.
   */
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  pageNumber?: number; // defaults to 1
}

export interface ErrorResponse {
  errorCode: string; // e.g. "INVALID_REQUEST"
  errorMessage: string; // e.g. "The payment method is invalid."
  details?: Record<string, unknown>;
  statusCode: number; // e.g. 409
}

export interface APIError extends Error {
  errorResponse?: ErrorResponse;
}

export interface APIRequest {
  baseUrl: string;
  endPoint: string;
  params?: Record<string, unknown>;
  options: Record<string, unknown>;
  errorMessage: string;
  // we will assume true by default
  isJSONResponse?: boolean;
}

export const HTTP_METHODS = {
  PUT: 'PUT',
  GET: 'GET',
  POST: 'POST',
  DELETE: 'DELETE',
};

export const commonPostOptions = {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
  },
};

export const commonPutOptions = {
  method: 'PUT',
  headers: {
    'Content-Type': 'application/json',
  },
};

export const commonDeleteOptions = {
  method: 'DELETE',
  headers: {},
};

export const commonGetOptions = {
  method: 'GET',
  headers: {},
};

export const getCommonOptionsFromType = (type = HTTP_METHODS.GET): Record<string, unknown> => {
  let options;
  switch (type) {
    case HTTP_METHODS.PUT:
      options = commonPutOptions;
      break;
    case HTTP_METHODS.POST:
      options = commonPostOptions;
      break;
    case HTTP_METHODS.DELETE:
      options = commonDeleteOptions;
      break;
    default:
      options = commonGetOptions;
  }
  return options;
};

export const getOptionsWithAuth = (token: string, type = HTTP_METHODS.GET): Record<string, unknown> => {
  const commonOptions = getCommonOptionsFromType(type);
  return {
    ...commonOptions,
    headers: {
      ...(commonOptions.headers || {}),
      Authorization: `Bearer ${token}`,
    },
    // credentials: 'include' as RequestCredentials,
  };
};

export const generateBasicAuthHeader = (user: string, password: string): string => {
  const token = `${user}:${password}`;
  const hash = btoa(token);
  return `Basic ${hash}`;
};

const optionalParams = (params?: RequestParams): string => {
  if (!params) {
    return '';
  }
  let keyValues = '?';
  keyValues += `pageSize=${params.pageSize || 20}`;
  if (params.pageNumber) {
    keyValues += `&pageNumber=${params.pageNumber}`;
  }
  if (params.sortBy) {
    keyValues += `&sortBy=${params.sortBy}`;
    if (params.sortOrder) {
      keyValues += `|${params.sortOrder}`;
    }
  }
  Object.keys(params).forEach((key) => {
    if (key !== 'pageSize' && key !== 'pageNumber' && key !== 'sortBy' && key !== 'sortOrder') {
      keyValues += `&${key}=${params[key]}`;
    }
  });
  return keyValues;
};

export const getCacheKey = (baseUrl: string, endPoint: string, params?: Record<string, unknown>): string =>
  `${baseUrl}${endPoint}${optionalParams(params)}`;

export const apiCall = async <T>(apiRequest: APIRequest): Promise<T> => {
  const { baseUrl, endPoint, options } = apiRequest;
  let adjustedBase = baseUrl;
  if (endPoint.startsWith('/') && adjustedBase.endsWith('/')) {
    adjustedBase = adjustedBase.substring(0, adjustedBase.length - 1);
  }
  const url = `${adjustedBase}${endPoint}${optionalParams(apiRequest.params)}`;
  const response = await fetch(url, options);
  if (response.status >= 400) {
    const detail = await response.text();
    const apiError: APIError = new Error(apiRequest.errorMessage) as APIError;

    let errorResponse: ErrorResponse | undefined;
    try {
      errorResponse = JSON.parse(detail) as ErrorResponse;
    } catch (err) {
      // If not valid JSON, leave errorResponse undefined
    }

    apiError.errorResponse = errorResponse;

    logger.error(`[apiCall] ${apiRequest.errorMessage}, Bad response from server ${detail}`);

    throw apiError;
  }
  if (apiRequest.isJSONResponse === false) {
    return Promise.resolve() as unknown as T;
  }
  return response.json();
};
